/* eslint-disable comma-dangle */
/* eslint-disable operator-linebreak */
import axios from "axios";
import { setSuccessLogout } from "../contexts/UserContext/actions";
import {
    ADMIN_AUTH_API_HEADERS,
    applyAdminAuthHeaders,
    getAdminApiClientConfig,
} from "./adminAuth";

const REDIRECT_TIMEOUT = 5000;
const REFRESH_TIMEOUT = 15000;
const BILLING_PATHS = [
    "/settings?menu=billing",
    "/subscription-billing",
    "/subscription/billing",
    "/billing",
    "/checkout",
];

const apiClient = axios.create(getAdminApiClientConfig());

Object.assign(apiClient.defaults.headers.common, ADMIN_AUTH_API_HEADERS);

const getBrowserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return undefined;
    }
};

apiClient.interceptors.request.use((config) => {
    applyAdminAuthHeaders(config);
    const timezone = getBrowserTimezone();
    if (timezone) {
        // eslint-disable-next-line no-param-reassign
        config.headers["X-Client-Timezone"] = timezone;
    }
    return config;
});

let isRedirecting = false;
let isRefreshing = false;
let failedQueue = [];
let interceptorsAttached = false;

const processQueue = (error) => {
    failedQueue.forEach(({ resolve, reject, config }) => {
        if (error) {
            reject(error);
        } else {
            resolve(config);
        }
    });
    failedQueue = [];
};

const shouldRedirect = (redirectPath) => {
    if (isRedirecting) return false;
    const currentPath = window.location.pathname + window.location.search;
    return !BILLING_PATHS.some((path) => currentPath.includes(path) || redirectPath.includes(path));
};

const safeRedirect = (path) => {
    if (!shouldRedirect(path)) return;

    try {
        isRedirecting = true;
        sessionStorage.setItem("redirectedForBilling", "true");

        const redirectUrl = path.includes("?") ? `${path}&_t=${Date.now()}` : `${path}?_t=${Date.now()}`;

        window.location.href = redirectUrl;

        setTimeout(() => {
            isRedirecting = false;
        }, REDIRECT_TIMEOUT);
    } catch (_err) {
        isRedirecting = false;
    }
};

const refreshAccessToken = async () => {
    const refreshClient = axios.create({
        ...getAdminApiClientConfig(),
        timeout: REFRESH_TIMEOUT,
    });

    const refreshResponse = await refreshClient.post("/api/auth/refresh-token", {});

    if (!refreshResponse?.data?.success) {
        throw new Error("Token refresh failed");
    }
};

const handlePaymentRequired = (error) => {
    sessionStorage.setItem(
        "subscriptionError",
        JSON.stringify({
            message: error.response.data.message || "Your subscription has expired",
            code: error.response.data.code || "SUBSCRIPTION_EXPIRED",
            timestamp: new Date().toISOString(),
        }),
    );

    sessionStorage.setItem("isRedirectingToBilling", "true");
    const redirectPath = error.response.data.redirectTo || "/settings?menu=billing";
    safeRedirect(redirectPath);

    return Promise.resolve({
        _isBillingRedirect: true,
        status: 200,
        data: { redirecting: true },
    });
};

const isAuthenticationError = (error) =>
    error.response?.status === 401 ||
    (error.response?.status === 500 &&
        (error.response?.data?.message === "jwt expired" || error.response?.data?.message === "jwt malformed"));

const handleAuthFailure = (dispatch, navigate, error) => {
    dispatch(setSuccessLogout());

    // `navigate` is the react-router v5 history object (`useHistory()`), not a v6 navigate function.
    if (navigate && shouldRedirect("/login")) {
        navigate.replace("/login", {
            message: error.response?.data?.message || "Authentication failed. Please log in again.",
        });
    }
};

const isAuthExemptPath = (url = "") => {
    const path = url.split("?")[0];
    return (
        path.endsWith("/api/auth/login") ||
        path.endsWith("api/auth/login") ||
        path.includes("/api/auth/logout") ||
        path.includes("api/auth/logout") ||
        path.includes("/api/auth/refresh-token") ||
        path.includes("api/auth/refresh-token")
    );
};

export const logoutAdmin = () => apiClient.post("/api/auth/logout");

export const setupAxiosInterceptors = (dispatch, navigate) => {
    const wasRedirected = sessionStorage.getItem("redirectedForBilling");
    if (wasRedirected) {
        sessionStorage.removeItem("redirectedForBilling");
    }

    if (interceptorsAttached) {
        return;
    }
    interceptorsAttached = true;

    apiClient.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            if (error.response?.status === 402) {
                return handlePaymentRequired(error);
            }

            if (!isAuthenticationError(error) || isAuthExemptPath(originalRequest?.url)) {
                return Promise.reject(error);
            }

            if (originalRequest._retry) {
                handleAuthFailure(dispatch, navigate, error);
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (config) => resolve(apiClient.request(config)),
                        reject,
                        config: originalRequest,
                    });
                });
            }

            isRefreshing = true;

            try {
                await refreshAccessToken();
                processQueue(null);
                isRefreshing = false;
                return apiClient.request(originalRequest);
            } catch (refreshError) {
                isRefreshing = false;
                processQueue(refreshError);

                if (refreshError.response?.status === 402) {
                    const redirectPath = refreshError.response.data.redirectTo || "/settings?menu=billing";
                    safeRedirect(redirectPath);
                    return Promise.resolve({
                        _isBillingRedirect: true,
                        status: 200,
                        data: { redirecting: true },
                    });
                }

                if (
                    refreshError.response?.status === 401 ||
                    refreshError.response?.status === 403
                ) {
                    handleAuthFailure(dispatch, navigate, refreshError);
                }

                return Promise.reject(refreshError);
            }
        },
    );
};

export default apiClient;
